import classNames from 'classnames'
import { ReactElement, useMemo } from 'react'
import DashboardHeader from 'app/components/analytics/DashboardHeader'
import DashboardTabs from 'app/components/analytics/DashboardTabs'
import LeadsSection from 'app/components/analytics/LeadsSection'
import OverviewSection from 'app/components/analytics/OverviewSection'
import ScheduleRateSection from 'app/components/analytics/ScheduleRateSection'
import { PageScroll } from 'app/components/layout/PageScroll'
import SimpleTable from 'app/components/table/SimpleTable'
import { Toggle } from 'app/components/Toggle'
import { isSMCOrg } from 'lib/isSMCOrg'
import type { UseMarketingReturn } from './useMarketing'
import { createMarketingChannelFetcher } from './channelOptions'

export type MarketingShadcnComponentProps = UseMarketingReturn

export function MarketingShadcnComponent(props: MarketingShadcnComponentProps): ReactElement {
  const {
    user,
    channel,
    filters,
    fromDate,
    toDate,
    compareFromDate,
    compareToDate,
    isComparingToGoals,
    dontCompare,
    platform,
    showDisqualified,
    openTab,
    canViewStaffBreakdown,
    summary,
    compareTo,
    locationRows,
    isLocationsLoading,
    scorecardStaffRows,
    isScorecardStaffLoading,
    locationColumns,
    staffColumns,
    locationIds,
    integrationStatus,
    defaultFilters,
    dateChangeHandler,
    handleTabChange,
    handleShowDisqualifiedToggle,
    setFilters,
    setLocationSearchQuery,
    setStaffSearchQuery,
  } = props

  const channelFetcher = useMemo(
    () => createMarketingChannelFetcher({ fromDate, toDate }, locationIds, integrationStatus),
    [fromDate, toDate, locationIds, integrationStatus, user?.orgId, user?.workspaceId],
  )
  // Remount the option controls when scope changes, so an old list cannot survive an empty response.
  const channelScopeKey = JSON.stringify([
    fromDate,
    toDate,
    locationIds,
    integrationStatus,
    user?.orgId,
    user?.workspaceId,
  ])

  return (
    <>
      <PageScroll
        contentClassName="pb-14 pt-8"
        header={
          <>
            <DashboardHeader
              title="Marketing"
              defaultChannel={channel}
              channelFetcher={channelFetcher}
              channelScopeKey={channelScopeKey}
              dateChangeHandler={dateChangeHandler}
              onFiltersChange={setFilters}
              limitDateRangeToMonth={!!user && isSMCOrg(user.orgId)}
              hideCompare={false}
              defaultDateRange={`this_month`}
              hideAdFilters={openTab === `Staff Breakdown`}
              extraButtons={
                <>
                  <Toggle
                    showLabelOnLeft={true}
                    label="Show Disqualified:"
                    defaultValue={showDisqualified}
                    handleToggle={handleShowDisqualifiedToggle}
                  />
                </>
              }
            />
            <DashboardTabs
              openTab={openTab}
              onSetOpenTab={handleTabChange}
              tabOptions={[`Summary`, `Location Breakdown`, ...(canViewStaffBreakdown ? [`Staff Breakdown`] : [])]}
            />
          </>
        }
      >
        <div className={classNames(openTab === `Summary` ? `block` : `hidden`, `flex flex-col gap-y-6 md:gap-y-12`)}>
          <OverviewSection
            funnelMetrics={summary}
            compareToMetrics={compareTo}
            fromDate={fromDate}
            toDate={toDate}
            isComparingToGoals={isComparingToGoals}
            dontCompare={dontCompare}
            showDisqualified={showDisqualified}
            defaultFilters={defaultFilters}
            platform={platform ? decodeURIComponent(platform) : undefined}
          />
          <LeadsSection
            leadsCount={summary?.leads ?? 0}
            summary={summary}
            compareTo={compareTo}
            channel={channel}
            fromDate={fromDate}
            toDate={toDate}
            locationIds={locationIds}
            integrationStatus={integrationStatus}
            dontCompare={dontCompare}
            filters={filters}
            platform={platform ? decodeURIComponent(platform) : undefined}
            defaultFilters={defaultFilters}
            showDisqualified={showDisqualified}
          />
          <ScheduleRateSection
            channel={channel}
            fromDate={fromDate}
            toDate={toDate}
            compareFromDate={compareFromDate}
            compareToDate={compareToDate}
            locationIds={locationIds}
            integrationStatus={integrationStatus}
            dontCompare={dontCompare}
            filters={filters}
            platform={platform ? decodeURIComponent(platform) : undefined}
            defaultFilters={defaultFilters}
          />
        </div>
        {openTab === `Location Breakdown` && (
          <div className={classNames(`flex h-full flex-col`)}>
            <SimpleTable
              activeSubpage={openTab}
              data={isLocationsLoading ? undefined : locationRows}
              columns={locationColumns}
              onSearchValueChange={(searchValue) => setLocationSearchQuery(searchValue)}
              showColumnOrdering
              enableExport
            />
          </div>
        )}
        {openTab === `Staff Breakdown` && canViewStaffBreakdown && (
          <div className={classNames(`flex h-full flex-col`)}>
            <SimpleTable
              activeSubpage={`${openTab}-v2`}
              data={isScorecardStaffLoading ? undefined : scorecardStaffRows}
              columns={staffColumns}
              onSearchValueChange={(searchValue) => setStaffSearchQuery(searchValue)}
              showColumnOrdering
              enableExport
            />
          </div>
        )}
      </PageScroll>
    </>
  )
}
